import React from 'react';
import toast from 'react-hot-toast';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import AuthImage from './AuthImage';
import { formatDate } from './constants';
import { fetchPhotoBlob } from './caseReviewApi';

function groupBySection(photos) {
    const grouped = {};
    (photos || []).forEach((photo) => {
        if (!grouped[photo.section]) grouped[photo.section] = [];
        grouped[photo.section].push(photo);
    });
    return grouped;
}

export default function PhotoGrid({ caseId, photos }) {
    const downloadAll = async () => {
        if (!photos || photos.length === 0) return;
        const t = toast.loading(`Downloading ${photos.length} photo(s)...`);
        let ok = 0;
        for (const photo of photos) {
            try {
                const blob = await fetchPhotoBlob(caseId, photo.section, photo.filename);
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${caseId}_${photo.section}_${photo.original_filename}`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                ok += 1;
                await new Promise((resolve) => setTimeout(resolve, 250));
            } catch (e) {
                // continue with the rest
            }
        }
        toast.success(`Downloaded ${ok} of ${photos.length} photo(s)`, { id: t });
    };

    if (!photos || photos.length === 0) {
        return <div className="text-center text-gray-500 py-8">No photos uploaded yet</div>;
    }

    const grouped = groupBySection(photos);

    return (
        <div>
            <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold">Photos ({photos.length})</h3>
                <button
                    type="button"
                    onClick={downloadAll}
                    className="bg-green-600 text-white px-3 py-1 rounded-md hover:bg-green-700 text-sm inline-flex items-center gap-1"
                >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                    Download All
                </button>
            </div>

            {Object.entries(grouped).map(([section, sectionPhotos]) => (
                <div key={section} className="mb-4">
                    <h4 className="font-medium text-sm text-gray-700 mb-2 capitalize">
                        {section.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                        {sectionPhotos.map((photo, index) => (
                            <div key={photo.id} className="border rounded overflow-hidden">
                                <AuthImage
                                    caseId={caseId}
                                    section={photo.section}
                                    filename={photo.filename}
                                    label={photo.original_filename}
                                    className="w-full h-32 object-cover"
                                />
                                <div className="p-2 bg-gray-50">
                                    <p className="text-xs text-gray-600 truncate font-mono">
                                        {caseId}_{section}_{index + 1}
                                    </p>
                                    <p className="text-xs text-gray-400">{formatDate(photo.uploaded_at)}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}
